import * as React from "react";
import { AlertCircle, LayoutGrid, Loader2 } from "lucide-react";
import {
  Button,
  Input,
  Label,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Switch,
  Textarea,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  SLUG_RULES,
  boundedNameSchema,
  deriveSlug,
  fieldError,
  slugProblemFor,
} from "@eduviaos/shared";
import {
  ZONE_ASSIGNMENT_LABELS,
  parseFacilities,
  trimmedOrNull,
  useCreateZone,
  useUpdateZone,
  type CreateZoneInput,
  type UpdateZoneInput,
  type Zone,
  type ZoneAssignmentMode,
} from "@/lib/seating/zones";

/**
 * Phase F-finale-7 Slice 7b.4a — Zone create / edit sheet.
 *
 * Right-side drawer mirroring `<StudentEditorSheet>`: keeps the Zones
 * `<DataTable>` visible behind the sheet while the admin types. The
 * zone's slug is derived from the name on create and never shown; it is
 * immutable afterwards. Edit mode PATCHes only the changed fields, and a
 * zero-diff save closes silently (no pointless write).
 *
 * The BE (`/api/zones`, `manage_zones`) re-validates the slug pattern and
 * field bounds; a duplicate slug is reported on the name field.
 */

const ZONE_MODES = Object.keys(ZONE_ASSIGNMENT_LABELS) as ZoneAssignmentMode[];
const ZONE_NAME_MAX = 120;
const DESCRIPTION_MAX = 2000;
const CAPACITY_MAX = 100_000;
const DUPLICATE_NAME_MESSAGE = "Another zone already has this name. Choose a different name.";

interface DraftState {
  name: string;
  description: string;
  capacity: string;
  facilities: string;
  assignment_mode: ZoneAssignmentMode;
  is_active: boolean;
}

const EMPTY_DRAFT: DraftState = {
  name: "",
  description: "",
  capacity: "",
  facilities: "",
  assignment_mode: "inherit",
  is_active: true,
};

const zoneNameSchema = boundedNameSchema(ZONE_NAME_MAX, "Zone name");

function capacityError(raw: string): string | null {
  if (raw.trim() === "") return null;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 0) return "Capacity must be a whole number, 0 or more.";
  if (n > CAPACITY_MAX) return `Capacity can't be more than ${CAPACITY_MAX.toLocaleString()}.`;
  return null;
}

function draftFromExisting(existing: Zone | null | undefined): DraftState {
  if (!existing) return EMPTY_DRAFT;
  return {
    name: existing.name,
    description: existing.description ?? "",
    capacity: existing.capacity > 0 ? String(existing.capacity) : "",
    facilities: existing.facilities.join(", "),
    assignment_mode: existing.assignment_mode ?? "inherit",
    is_active: existing.is_active,
  };
}

function buildCreatePayload(draft: DraftState, slug: string): CreateZoneInput {
  const payload: CreateZoneInput = {
    name: draft.name.trim(),
    slug,
  };
  const description = trimmedOrNull(draft.description);
  if (description) payload.description = description;
  const capacity = Number(draft.capacity);
  if (draft.capacity.trim() !== "" && Number.isFinite(capacity) && capacity >= 0) {
    payload.capacity = Math.trunc(capacity);
  }
  const facilities = parseFacilities(draft.facilities);
  if (facilities.length > 0) payload.facilities = facilities;
  payload.assignment_mode = draft.assignment_mode;
  return payload;
}

function buildUpdatePayload(draft: DraftState, existing: Zone): UpdateZoneInput {
  const payload: UpdateZoneInput = {};

  const name = draft.name.trim();
  if (name && name !== existing.name) payload.name = name;

  const description = trimmedOrNull(draft.description);
  if ((existing.description ?? null) !== description) payload.description = description;

  const capacity = draft.capacity.trim() === "" ? 0 : Math.trunc(Number(draft.capacity));
  if (Number.isFinite(capacity) && capacity >= 0 && capacity !== existing.capacity) {
    payload.capacity = capacity;
  }

  const facilities = parseFacilities(draft.facilities);
  const sameFacilities =
    facilities.length === existing.facilities.length &&
    facilities.every((f, i) => f === existing.facilities[i]);
  if (!sameFacilities) payload.facilities = facilities;

  if (draft.is_active !== existing.is_active) payload.is_active = draft.is_active;

  if (draft.assignment_mode !== (existing.assignment_mode ?? "inherit")) {
    payload.assignment_mode = draft.assignment_mode;
  }

  return payload;
}

export interface ZoneEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing?: Zone | null;
  /** Slugs of zones already loaded, so a duplicate name is caught before the round-trip. */
  takenSlugs?: readonly string[];
  onSaved?: (saved: Zone) => void;
}

export function ZoneEditorSheet({
  open,
  onOpenChange,
  existing,
  takenSlugs,
  onSaved,
}: ZoneEditorSheetProps) {
  const isEdit = Boolean(existing);

  const [draft, setDraft] = React.useState<DraftState>(() => draftFromExisting(existing));
  const [showErrors, setShowErrors] = React.useState(false);
  const [serverNameError, setServerNameError] = React.useState<string | null>(null);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setDraft(draftFromExisting(existing));
      setShowErrors(false);
      setServerNameError(null);
      setSubmitError(null);
    }
  }, [open, existing]);

  const createMutation = useCreateZone();
  const updateMutation = useUpdateZone();
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const handleField = <K extends keyof DraftState>(key: K, value: DraftState[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const derivedSlug = isEdit ? "" : deriveSlug(draft.name, SLUG_RULES.zone);
  const nameError =
    serverNameError ??
    fieldError(zoneNameSchema, draft.name) ??
    (isEdit
      ? null
      : slugProblemFor(derivedSlug, SLUG_RULES.zone, { noun: "zone", taken: takenSlugs }));
  const capacityProblem = capacityError(draft.capacity);
  const hasErrors = Boolean(nameError || capacityProblem);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setShowErrors(true);
    if (hasErrors || isSubmitting) return;

    try {
      if (existing) {
        const body = buildUpdatePayload(draft, existing);
        if (Object.keys(body).length === 0) {
          onOpenChange(false);
          return;
        }
        const saved = await updateMutation.mutateAsync({ id: existing.id, body });
        onSaved?.(saved);
        toast.success(`Saved changes to ${saved.name}.`);
        onOpenChange(false);
      } else {
        const saved = await createMutation.mutateAsync(buildCreatePayload(draft, derivedSlug));
        onSaved?.(saved);
        toast.success(`Created zone ${saved.name}.`);
        onOpenChange(false);
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === "ZONE_DUPLICATE_SLUG") {
        setServerNameError(DUPLICATE_NAME_MESSAGE);
        return;
      }
      const message =
        err instanceof ApiError
          ? friendlyApiMessage(err)
          : err instanceof Error
            ? err.message
            : "Couldn't save the zone. Please retry.";
      setSubmitError(message);
    }
  };

  const title = isEdit ? "Edit zone" : "Add zone";
  const description = isEdit
    ? `Update ${existing?.name ?? "this zone"}.`
    : "Create a floor, room, or section members can be seated in.";
  const visibleNameError = showErrors || serverNameError ? nameError : null;
  const visibleCapacityError = showErrors ? capacityProblem : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[480px]"
        data-testid="zone-editor-sheet"
      >
        <SheetHeader className="border-border/60 border-b px-6 pb-4 pt-6">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="bg-primary/10 text-primary mt-0.5 grid size-10 shrink-0 place-items-center rounded-lg"
            >
              <LayoutGrid className="size-5" />
            </span>
            <div className="flex-1">
              <SheetTitle className="tracking-tight">{title}</SheetTitle>
              <SheetDescription className="mt-1 text-xs">{description}</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
          data-testid="zone-editor-form"
        >
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            {submitError ? (
              <div
                role="alert"
                className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
              >
                <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                <span className="flex-1">{submitError}</span>
              </div>
            ) : null}

            <Field label="Zone name" htmlFor="zone-name" required error={visibleNameError}>
              <Input
                id="zone-name"
                value={draft.name}
                maxLength={ZONE_NAME_MAX}
                required
                autoFocus
                aria-invalid={visibleNameError ? true : undefined}
                aria-describedby={visibleNameError ? "zone-name-error" : undefined}
                onBlur={() => setShowErrors(true)}
                onChange={(e) => {
                  setServerNameError(null);
                  handleField("name", e.target.value);
                }}
                placeholder="Ground Floor"
              />
            </Field>

            <Field label="Description" htmlFor="zone-description">
              <Textarea
                id="zone-description"
                value={draft.description}
                maxLength={DESCRIPTION_MAX}
                rows={2}
                onChange={(e) => handleField("description", e.target.value)}
                placeholder="Quiet study area on the ground floor."
              />
            </Field>

            <Field
              label="Capacity"
              htmlFor="zone-capacity"
              helper="Number of seats this zone can hold. Leave blank if not tracked."
              error={visibleCapacityError}
            >
              <Input
                id="zone-capacity"
                type="number"
                inputMode="numeric"
                min={0}
                max={CAPACITY_MAX}
                step={1}
                aria-invalid={visibleCapacityError ? true : undefined}
                aria-describedby={visibleCapacityError ? "zone-capacity-error" : undefined}
                value={draft.capacity}
                onChange={(e) => handleField("capacity", e.target.value)}
                placeholder="40"
              />
            </Field>

            <Field
              label="Seat rule"
              htmlFor="zone-seat-rule"
              helper="Permanent rooms stay assigned. First-come rooms are booked by the shift. Centre default follows the setting on Seat configuration."
            >
              <select
                id="zone-seat-rule"
                value={draft.assignment_mode}
                onChange={(e) =>
                  handleField("assignment_mode", e.target.value as ZoneAssignmentMode)
                }
                className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
              >
                {ZONE_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {ZONE_ASSIGNMENT_LABELS[mode]}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label="Facilities"
              htmlFor="zone-facilities"
              helper="Comma-separated, e.g. wifi, ac, lockers."
            >
              <Input
                id="zone-facilities"
                value={draft.facilities}
                onChange={(e) => handleField("facilities", e.target.value)}
                placeholder="wifi, ac, lockers"
              />
            </Field>

            {isEdit ? (
              <label className="border-border/60 bg-muted/20 flex items-start gap-3 rounded-md border px-3 py-2.5">
                <Switch
                  checked={draft.is_active}
                  onCheckedChange={(v) => handleField("is_active", v)}
                  aria-label="Active"
                />
                <div className="grid gap-0.5">
                  <span className="text-sm font-medium">Active</span>
                  <span className="text-muted-foreground text-xs">
                    Inactive zones are hidden from new bookings and allocations.
                  </span>
                </div>
              </label>
            ) : null}
          </div>

          <div className="border-border/60 bg-card/40 flex items-center justify-end gap-2 border-t px-6 py-4 backdrop-blur">
            <SheetClose asChild>
              <Button variant="ghost" type="button" disabled={isSubmitting}>
                Cancel
              </Button>
            </SheetClose>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <Loader2 aria-hidden="true" className={cn("size-4 animate-spin")} />
              ) : null}
              {isEdit ? "Save changes" : "Add zone"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

interface FieldProps {
  label: string;
  htmlFor: string;
  required?: boolean;
  helper?: string;
  error?: string | null;
  children: React.ReactNode;
}

function Field({ label, htmlFor, required, helper, error, children }: FieldProps) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="text-xs">
        {label}
        {required ? <span className="text-destructive ml-0.5">*</span> : null}
      </Label>
      <div className="mt-1">{children}</div>
      {error ? (
        <p id={`${htmlFor}-error`} className="text-destructive mt-1 text-xs">
          {error}
        </p>
      ) : helper ? (
        <p className="text-muted-foreground mt-1 text-xs">{helper}</p>
      ) : null}
    </div>
  );
}

function friendlyApiMessage(err: ApiError): string {
  switch (err.code) {
    case "AZ_PERMISSION_DENIED":
      return "You don't have permission to manage zones. Ask an admin to grant manage_zones.";
    default:
      return err.message || "Couldn't save the zone. Please retry.";
  }
}
